<template>
    <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltipOn, attrs }">
            <v-menu
                v-model="menuOpen"
                :close-on-content-click="false"
                transition="slide-y-transition"
                offset-y
                content-class="round-card"
                nudge-right="30"
                max-height="800"
                min-width="620"
                max-width="620">

                <template v-slot:activator="{ on: menuOn }">
                    <v-text-field
                        ref="searchField"
                        v-bind="attrs"
                        v-on="{ ...tooltipOn, ...menuOn }"
                        v-model="search"
                        :label="searchLabel"
                        :loading="loading"
                        class="mx-4"
                        flat
                        hide-details
                        solo-inverted
                        dark
                        clearable
                        prepend-icon="mdi-magnify"
                        @input="onSearchInput"
                        @click:clear="onClear">
                    </v-text-field>
                </template>

                <v-card v-if="hasResults" class="round-card">
                    <v-list dense>
                        <template v-for="(group, groupIndex) in groupedResults">
                            <!-- Header gruppo -->
                            <v-subheader :key="`header-${groupIndex}`" class="font-weight-bold primary--text">
                                <v-icon left small color="primary">{{ group.icon }}</v-icon>
                                {{ group.label }} ({{ group.total }})
                            </v-subheader>

                            <!-- Items del gruppo -->
                            <transition-group name="list-expand" tag="div">
                                <v-list-item
                                    v-for="item in group.hits"
                                    :key="`item-${item.id}`"
                                    @click="navigate(item)"
                                    @mousedown.middle.prevent="openInNewTab(item)">
                                    <v-list-item-icon>
                                        <v-icon>{{ item.icon }}</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content class="ml-2">
                                        <v-list-item-title v-html="highlight(item.title)"></v-list-item-title>
                                        <v-list-item-subtitle v-if="item.subtitle">
                                            {{ item.subtitle }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>
                            </transition-group>

                            <!-- Mostra tutti / Mostra meno -->
                            <v-list-item
                                v-if="group.hasMore"
                                :key="`expand-${groupIndex}`"
                                @click="toggleExpand(group.modelName)"
                                class="background-1">
                                <v-list-item-content>
                                    <v-list-item-title class="text-center primary--text font-weight-medium">
                                        <v-icon small color="primary" class="mr-1">
                                            {{ group.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                        </v-icon>
                                        {{ group.isExpanded ? 'Mostra meno' : `Mostra tutti (${group.total})` }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>

                            <!-- Divider -->
                            <v-divider
                                v-if="groupIndex < groupedResults.length - 1"
                                :key="`divider-${groupIndex}`"
                                class="my-2">
                            </v-divider>
                        </template>
                    </v-list>
                </v-card>

                <v-card v-else-if="search && search.length >= 4 && !loading">
                    <v-list-item>
                        <v-list-item-content>
                            <v-list-item-title class="text-center grey--text">
                                Nessun risultato trovato
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-card>

                <v-card v-else-if="search && search.length < 4">
                    <v-list-item>
                        <v-list-item-content>
                            <v-list-item-title class="text-center grey--text">
                                Digita almeno 4 caratteri per iniziare la ricerca
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-card>
            </v-menu>
        </template>
        <span>Premi Ctrl+K per cercare rapidamente</span>
    </v-tooltip>
</template>

<script>
export default {
    name: 'global-search',

    data() {
        return {
            search: null,
            menuOpen: false,
            loading: false,
            results: [],
            debounceTimer: null,
            expandedGroups: {}
        }
    },

    computed: {
        searchLabel() {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
            const shortcut = isMac ? '⌘K' : 'Ctrl+K'
            return `Cerca ovunque... (${shortcut})`
        },

        groupedResults() {
            return this.results
                .map(result => {
                    const meta = this.parseIndexName(result.index)
                    const isExpanded = this.expandedGroups[meta.modelName] || false
                    const displayLimit = isExpanded ? result.total : 5

                    return {
                        ...meta,
                        total: result.total,
                        isExpanded,
                        hasMore: result.total > 5,
                        hits: result.hits.slice(0, displayLimit).map(hit => ({
                            id: `${meta.modelName}_${hit.id}`,
                            recordId: hit.id,
                            title: this.getTitle(hit, meta.modelName),
                            subtitle: this.getSubtitle(hit, meta.modelName),
                            routeName: meta.routeName,
                            routeParams: { id: hit.id },
                            icon: meta.icon,
                            raw: hit
                        }))
                    }
                })
                .filter(group => group.hits.length > 0)
        },

        hasResults() {
            return this.groupedResults.length > 0
        }
    },

    watch: {
        search(val) {
            // Cancella sempre il timer precedente
            clearTimeout(this.debounceTimer)

            if (!val || val.length < 4) {
                this.results = []
                this.menuOpen = false
                return
            }

            this.debounceTimer = setTimeout(() => {
                this.performSearch(val)
            }, 300)
        }
    },

    mounted() {
        window.addEventListener('keydown', this.handleShortcut)
    },

    beforeDestroy() {
        window.removeEventListener('keydown', this.handleShortcut)
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }
    },

    methods: {
        async performSearch(query) {
            // Trim della stringa per evitare che spazi iniziali/finali causino errori di validazione
            const trimmedSearch = this.search ? this.search.trim() : ''

            // Verifica il valore ATTUALE di this.search al momento dell'esecuzione
            // per evitare race condition se l'utente ha modificato la stringa
            // dopo che il debounce è partito ma prima che performSearch esegua
            if (!trimmedSearch || trimmedSearch.length < 4) {
                return
            }

            this.loading = true
            this.menuOpen = true

            try {
                const response = await this.axios.post('/meilisearch/global-search', {
                    q: trimmedSearch,  // Usa il valore trimmato
                    limit_per_index: 20  // Recupera più risultati per permettere l'espansione
                })

                if (response.data.success) {
                    this.results = response.data.results
                } else {
                    this.results = []
                }
            } catch (error) {
                console.error('Global search error:', error)
                this.$store.commit('setSnackbarError')
                this.results = []
            } finally {
                this.loading = false
            }
        },

        toggleExpand(modelName) {
            this.$set(this.expandedGroups, modelName, !this.expandedGroups[modelName])
        },

        onSearchInput(val) {
            // Il watch si occupa già di gestire il cambio
        },

        onClear() {
            this.search = null
            this.results = []
            this.expandedGroups = {}
            this.menuOpen = false
        },

        parseIndexName(indexName) {
            const parts = indexName.split('_')
            const modelName = parts[parts.length - 1]

            // Route name mapping per tutti i modelli searchable
            const routeNameMap = {
                // CRM
                'companies': 'companies-show',
                'companydestinations': 'companydestinations-show',
                'companycontacts': 'companycontacts-show',
                'suppliercontacts': 'suppliercontacts-show',
                'suppliers': 'suppliers-show',
                'leads': 'leads-show',
                'prospects': 'prospects-show',

                // Projects & Opportunities
                'projects': 'projects-show',
                'opportunities': 'opportunities-show',

                // Tasks & Quotations
                'tasks': 'tasks-show',
                'quotations': 'quotations-show',

                // Subscriptions & Assets
                'subscriptions': 'subscriptions-show',
                'assets': 'assets-show',
                'credentials': 'credentials-show',

                // Orders & DDTs
                'saleorders': 'saleorders-show',
                'purchaseorders': 'purchaseorders-show',
                'ddts': 'ddts-show',
                'purchaseddts': 'purchaseddts-show',

                // Invoices
                'saleinvoices': 'documents-saleinvoice-show',
                'proformainvoices': 'documents-proformainvoice-show',
                'purchaseinvoices': 'documents-purchaseinvoice-show',
                'creditnotes': 'documents-creditnote-show',

                // Tickets module
                'troubles': 'troubles-show',
                'contracts': 'contracts-show',
                'contractors': 'contractors-show',

                // Agents
                'agents': 'agents-show'
            }

            return {
                modelName,
                routeName: routeNameMap[modelName] || null,
                icon: this.getIconForModel(modelName),
                label: this.$trans(modelName)
            }
        },

        getIconForModel(modelName) {
            const icons = {
                // CRM
                'companies': 'mdi-domain',
                'companydestinations': 'mdi-map-marker',
                'companycontacts': 'mdi-account',
                'suppliercontacts': 'mdi-account',
                'suppliers': 'mdi-truck',
                'leads': 'mdi-account-plus',
                'prospects': 'mdi-account-clock',

                // Projects & Opportunities
                'projects': 'mdi-folder-multiple',
                'opportunities': 'mdi-briefcase',

                // Tasks & Quotations
                'tasks': 'mdi-check-circle',
                'quotations': 'mdi-file-document-outline',

                // Subscriptions & Assets
                'subscriptions': 'mdi-repeat',
                'assets': 'mdi-package-variant',

                // Orders & DDTs
                'saleorders': 'mdi-cart',
                'purchaseorders': 'mdi-cart-arrow-down',
                'ddts': 'mdi-truck-delivery',
                'purchaseddts': 'mdi-truck-check',

                // Invoices
                'saleinvoices': 'mdi-file-document',
                'proformainvoices': 'mdi-file-document-edit',
                'purchaseinvoices': 'mdi-file-document-arrow-right',
                'creditnotes': 'mdi-file-document-minus',

                // Tickets module
                'troubles': 'mdi-ticket',
                'contracts': 'mdi-file-sign',
                'contractors': 'mdi-hard-hat',

                // Agents
                'agents': 'mdi-account-tie'
            }
            return icons[modelName] || 'mdi-file-document'
        },

        getTitle(hit, modelName) {
            const titleFields = {
                // CRM
                'companies': hit.business_name,
                'suppliers': hit.business_name,
                'leads': hit.business_name || `${hit.name || ''} ${hit.surname || ''}`.trim(),
                'prospects': hit.business_name || `${hit.name || ''} ${hit.surname || ''}`.trim(),
                'companydestinations': hit.name,
                'companycontacts': `${hit.name || ''} ${hit.surname || ''}`.trim(),
                'suppliercontacts': `${hit.name || ''} ${hit.surname || ''}`.trim(),

                // Projects & Opportunities
                'projects': hit.name,
                'opportunities': hit.name,

                // Tasks & Quotations
                'tasks': hit.title,
                'quotations': hit.name,

                // Subscriptions & Assets
                'subscriptions': hit.name,
                'assets': hit.name,

                // Orders & DDTs
                'saleorders': hit.display_name,
                'purchaseorders': hit.display_name,
                'ddts': hit.display_name,
                'purchaseddts': hit.display_name,

                // Invoices
                'saleinvoices': hit.display_name,
                'proformainvoices': hit.display_name,
                'purchaseinvoices': hit.display_name,
                'creditnotes': hit.display_name,

                // Tickets module
                'troubles': hit.title,
                'contracts': hit.name,
                'contractors': hit.business_name,

                // Agents
                'agents': hit.name
            }
            return titleFields[modelName] || hit.name || hit.title || 'N/A'
        },

        getSubtitle(hit, modelName) {
            const subtitleFields = {
                // CRM
                'companies': hit.vat_number,
                'suppliers': hit.vat_number,
                'leads': hit.primary_email,
                'prospects': hit.primary_email,
                'companydestinations': hit.address,
                'companycontacts': hit.primary_email,

                // Projects & Opportunities
                'projects': this.truncate(hit.description_normalized, 50),
                'opportunities': this.truncate(hit.description_normalized, 50),

                // Tasks & Quotations
                'tasks': this.truncate(hit.description_normalized, 50),
                'quotations': this.truncate(hit.description_normalized, 50),

                // Subscriptions & Assets
                'subscriptions': this.truncate(hit.description_normalized, 50),
                'assets': this.truncate(hit.description_normalized, 50),

                // Orders & DDTs
                'saleorders': this.truncate(hit.description_normalized, 50),
                'purchaseorders': this.truncate(hit.description_normalized, 50),
                'ddts': this.truncate(hit.description_normalized, 50),
                'purchaseddts': this.truncate(hit.description_normalized, 50),

                // Invoices
                'saleinvoices': this.truncate(hit.description_normalized, 50),
                'proformainvoices': this.truncate(hit.description_normalized, 50),
                'purchaseinvoices': this.truncate(hit.description_normalized, 50),
                'creditnotes': this.truncate(hit.description_normalized, 50),

                // Tickets module
                'troubles': this.truncate(hit.request_normalized, 50),
                'contracts': this.truncate(hit.description_normalized, 50),
                'contractors': this.truncate(hit.description_normalized, 50),

                // Agents
                'agents': this.truncate(hit.description_normalized, 50)
            }
            return subtitleFields[modelName] || ''
        },

        truncate(text, length) {
            if (!text) return ''
            return text.length > length ? text.substring(0, length) + '...' : text
        },

        highlight(text) {
            if (!this.search || !text) return text

            const regex = new RegExp(`(${this.escapeRegex(this.search)})`, 'gi')
            return text.replace(regex, '<strong class="primary--text">$1</strong>')
        },

        escapeRegex(string) {
            return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        },

        navigate(hit) {
            if (hit.routeName) {
                this.$router.push({
                    name: hit.routeName,
                    params: hit.routeParams || {}
                })
            }
            this.search = null
            this.results = []
            this.expandedGroups = {}
            this.menuOpen = false
        },

        openInNewTab(hit) {
            if (!hit.routeName) return
            const route = this.$router.resolve({
                name: hit.routeName,
                params: hit.routeParams || {}
            })
            window.open(route.href, '_blank')
        },

        handleShortcut(event) {
            if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
                event.preventDefault()
                this.focusSearchField()
            }
        },

        focusSearchField() {
            if (this.$refs.searchField) {
                this.$refs.searchField.focus()
            }
        }
    }
}
</script>

<style scoped>
.v-list-item__title strong {
    font-weight: bold;
    color: var(--v-primary-base);
    background-color: rgba(var(--v-primary-base), 0.1);
    padding: 0 2px;
}

.v-subheader {
    font-size: 0.875rem;
    letter-spacing: 0.0892857143em;
    text-transform: uppercase;
}

/* Animazioni per l'espansione/collasso della lista */
.list-expand-enter-active {
    animation: expand-in 0.3s ease-out;
}

.list-expand-leave-active {
    animation: expand-out 0.2s ease-in;
}

.list-expand-move {
    transition: transform 0.3s ease;
}

@keyframes expand-in {
    0% {
        opacity: 0;
        max-height: 0;
        transform: translateY(-10px);
    }
    100% {
        opacity: 1;
        max-height: 100px;
        transform: translateY(0);
    }
}

@keyframes expand-out {
    0% {
        opacity: 1;
        max-height: 100px;
        transform: translateY(0);
    }
    100% {
        opacity: 0;
        max-height: 0;
        transform: translateY(-10px);
    }
}
</style>
